import type { ReactNode } from 'react';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import type { TaskListResponse } from '@mybusinessmate/schema';
import { clientStatus, CLIENT_GROUP_ORDER, groupTasks, staffStatus, STAFF_GROUP_ORDER } from '../../lib/task-display';
import { InfiniteList } from '../ui/infinite-list';
import { ClientTaskRow, StaffTaskRow, TaskRowSkeleton } from './task-row';

type Audience = 'staff' | 'client';

const VIEW = {
  staff: { status: staffStatus, order: STAFF_GROUP_ORDER },
  client: { status: clientStatus, order: CLIENT_GROUP_ORDER },
} as const;

/**
 * A project's tasks in sections ("Waiting for client", "In progress"...). The API pages by recent activity; within
 * what is loaded the sections are kept contiguous, so each header is shown once, above its first task.
 */
export function TaskList({
  query,
  audience,
  emptyState,
  className,
}: {
  query: UseInfiniteQueryResult<InfiniteData<TaskListResponse>>;
  audience: Audience;
  emptyState: ReactNode;
  className?: string;
}) {
  const { status, order } = VIEW[audience];
  const loaded = query.data?.pages.flatMap((p) => p.items) ?? [];
  const sections = groupTasks(loaded, status, order);
  const items = sections.flatMap((s) =>
    s.tasks.map((task) => ({ task, title: s.title, first: task === s.tasks[0], count: s.tasks.length })),
  );

  return (
    <InfiniteList
      className={className}
      items={items}
      getKey={(i) => i.task.id}
      renderItem={(i) => (
        <>
          {i.first ? (
            audience === 'client' ? (
              <h3 className="flex items-baseline gap-(--mbm-space-2) pb-(--mbm-space-1) pl-(--mbm-space-3) pt-(--mbm-space-4) text-label-large text-text-primary first:pt-(--mbm-space-2)">
                {i.title}
                <span className="text-text-tertiary">{i.count}</span>
              </h3>
            ) : (
              <h3 className="flex items-baseline gap-(--mbm-space-2) px-(--mbm-space-6) pb-(--mbm-space-1) pt-(--mbm-space-5) text-label-small text-text-tertiary uppercase">
                {i.title}
                <span>{i.count}</span>
              </h3>
            )
          ) : null}
          {audience === 'staff' ? <StaffTaskRow task={i.task} /> : <ClientTaskRow task={i.task} />}
        </>
      )}
      hasNextPage={query.hasNextPage}
      isFetchingNextPage={query.isFetchingNextPage}
      fetchNextPage={() => void query.fetchNextPage()}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      emptyState={emptyState}
      skeletonRow={<TaskRowSkeleton />}
    />
  );
}
